/**
 * Export (TASKS T75, SPEC §8.8). **[DoD-13]**
 *
 * > One-tap export of the full review log and manual entries as both JSON and CSV. This is a systems
 * > learner; assume he will analyse it himself and design the schema to be legible without docs.
 *
 * "Legible without docs" is the whole specification, and it has consequences that look like
 * verbosity until you try to read the file six months later:
 *
 *  - **Every timestamp appears twice**, as epoch milliseconds and as ISO-8601. The first sorts and
 *    subtracts; the second can be read. Neither alone is enough for someone opening this in a
 *    spreadsheet a year from now.
 *  - **No abbreviations, and no shared column doing two jobs.** `attempt_advanced_interval`, not
 *    `adv`.
 *  - **Measured and logged minutes are separate columns and there is no total** (DoD-12). A
 *    spreadsheet can add them if it wants to; this file will not do it on his behalf and give the sum
 *    a standing it has not earned.
 *  - **Nothing is derived that he could derive.** The export carries facts, not the tracker's
 *    conclusions — a retention figure baked in at export time is one he cannot recompute if he
 *    disagrees with the definition.
 *
 * Pure. No clock, no store, no DOM.
 */

import type { Attempt, ItemRecord, ManualEntry, SessionRecord } from '../store/schema';
import { SCHEMA_VERSION } from '../store/schema';

export interface ExportInput {
  attempts: readonly Attempt[];
  manual: readonly ManualEntry[];
  sessions: readonly SessionRecord[];
  items: readonly ItemRecord[];
  telemetry: unknown;
  exportedAt: number;
}

/** ISO-8601 in UTC. Beside every epoch, never instead of one. */
export function isoOf(epochMs: number): string {
  return new Date(epochMs).toISOString();
}

// ---------------------------------------------------------------------------------------------
// JSON
// ---------------------------------------------------------------------------------------------

/**
 * The whole log, as JSON.
 *
 * `schemaVersion` and `exportedAt` are at the top so a file found later can identify itself. The
 * four record types stay in four arrays rather than being flattened into one stream of events: they
 * are different shapes, and merging them would mean inventing a discriminator column and losing the
 * types.
 */
export function exportJson(input: ExportInput): string {
  return JSON.stringify(
    {
      schemaVersion: SCHEMA_VERSION,
      exportedAt: input.exportedAt,
      exportedAtIso: isoOf(input.exportedAt),
      telemetry: input.telemetry,
      attempts: input.attempts.map((attempt) => ({
        ...attempt,
        atIso: isoOf(attempt.at),
      })),
      manualEntries: input.manual.map((entry) => ({
        ...entry,
        atIso: isoOf(entry.at),
      })),
      sessions: input.sessions.map((session) => ({
        ...session,
        startedAtIso: isoOf(session.startedAt),
        endedAtIso: isoOf(session.endedAt),
      })),
      // Items carry their scheduler memory, which is what makes an independent analysis of the
      // schedule possible at all.
      items: input.items.map((item) => ({
        ...item,
        memory: { ...item.memory, dueAtIso: isoOf(item.memory.dueAt) },
      })),
    },
    null,
    2,
  );
}

// ---------------------------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------------------------

/** RFC 4180: quote anything containing a comma, a quote or a newline, and double the quotes. */
export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function csvRow(cells: readonly unknown[]): string {
  return cells.map(csvCell).join(',');
}

export const ATTEMPT_COLUMNS = [
  'attempt_id',
  'item_id',
  'tier',
  'mode',
  'at_epoch_ms',
  'at_iso',
  'correct',
  'hinted',
  'is_repeat_within_run',
  'was_due_review',
  'latency_ms',
  'near_miss',
  'advanced_interval',
  'run_id',
] as const;

export const MANUAL_COLUMNS = [
  'entry_id',
  'component',
  'logged_minutes',
  'at_epoch_ms',
  'at_iso',
  'note',
  'derived_item_ids',
] as const;

export const SESSION_COLUMNS = [
  'run_id',
  'started_at_epoch_ms',
  'started_at_iso',
  'ended_at_epoch_ms',
  'ended_at_iso',
  'app_measured_minutes',
  'items_attempted',
  'items_banked',
] as const;

/**
 * Three CSVs, not one.
 *
 * A single file would need a `record_type` column and a union of every field, which is the shape
 * that is hardest to read and hardest to load. Each table is returned with its own header row and a
 * name, so a spreadsheet gets three sheets and a script gets three frames.
 */
export interface CsvTable {
  name: string;
  csv: string;
}

export function exportCsv(input: ExportInput): CsvTable[] {
  return [
    {
      name: 'attempts',
      csv: [
        csvRow(ATTEMPT_COLUMNS),
        ...input.attempts.map((attempt) =>
          csvRow([
            attempt.id,
            attempt.itemId,
            attempt.tier,
            attempt.mode,
            attempt.at,
            isoOf(attempt.at),
            attempt.correct,
            attempt.hinted,
            attempt.isRepeatWithinRun,
            attempt.wasDueReview,
            attempt.latencyMs,
            attempt.nearMiss ?? '',
            attempt.advancedInterval,
            attempt.runId,
          ]),
        ),
      ].join('\n'),
    },
    {
      name: 'manual-entries',
      csv: [
        csvRow(MANUAL_COLUMNS),
        ...input.manual.map((entry) =>
          csvRow([
            entry.id,
            entry.component,
            // Named `logged_`, and there is no `measured_` column in this table: the two never share
            // a column and never share a file row (DoD-12).
            entry.minutes,
            entry.at,
            isoOf(entry.at),
            entry.note ?? '',
            (entry.derivedItemIds ?? []).join(' '),
          ]),
        ),
      ].join('\n'),
    },
    {
      name: 'sessions',
      csv: [
        csvRow(SESSION_COLUMNS),
        ...input.sessions.map((session) =>
          csvRow([
            session.runId,
            session.startedAt,
            isoOf(session.startedAt),
            session.endedAt,
            isoOf(session.endedAt),
            session.appMeasuredMinutes,
            session.itemsAttempted,
            session.itemsBanked,
          ]),
        ),
      ].join('\n'),
    },
  ];
}

/** A filename that sorts chronologically and says what it is. */
export function exportFilename(kind: string, exportedAt: number): string {
  const stamp = isoOf(exportedAt).replace(/[:.]/g, '-').slice(0, 19);
  return `ronda-${kind}-${stamp}`;
}
